import { Search } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useDeleteEdicaoMutation, useGetListaEdicoesQuery, useReativarEdicaoMutation } from "shared/features/api/edicao/edicaoSlice";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from '@mui/icons-material/Restore';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DeleteModal } from "components/DeleteModal";

export const EdicaoList: React.FC = () => {
  const [ativo, setAtivo] = useState<boolean>(true);
  const [open, setOpen] = useState(false);
  const [idEdicaoDelete, setIdEdicaoDelete] = useState<number>();

  const { data: edicoes, isLoading: carregandoEdicoes } =
    useGetListaEdicoesQuery(ativo);

  const [deletarEdicao, {isLoading: carregandoDelecao}] = useDeleteEdicaoMutation();
  const [reativarEdicao, {isLoading: carregandoReativacao}] = useReativarEdicaoMutation();

  const [search, setSearch] = useState<string>("");
  const navigate = useNavigate();

  const edicoesFiltradas = useMemo(() => {
    return search.length > 0
      ? edicoes?.filter((edicao) =>
          edicao.nome.toLowerCase().includes(search.toLowerCase())
        )
      : edicoes;
  }, [edicoes, search]);

  const handleConfirmDelete = (id: number) => {
    setOpen(true);
    setIdEdicaoDelete(id);
  }

  const handleDelete = async (id: number) => {
    await toast.promise(
      deletarEdicao(id).unwrap(),
      {
          pending: "Carregando...",
          success: "Edição desativada com sucesso!",
          error: {
            render({ data }) {
              const response = data as {
                data?: {
                  errors?: string[];
                  message?: string;
                };
              };

              const errorMessage =
                response.data?.errors?.[0] ||
                response.data?.message ||
                "Erro ao desativar edição.";

              return errorMessage;
            },
          },
        }
    )
  }

  const handleRestore = async (id: number) => {
    await toast.promise(
      reativarEdicao(id).unwrap(),
      {
          pending: "Carregando...",
          success: "Edição reativada com sucesso!",
          error: {
            render({ data }) {
              const response = data as {
                data?: {
                  errors?: string[];
                  message?: string;
                };
              };

              const errorMessage =
                response.data?.errors?.[0] ||
                response.data?.message ||
                "Erro ao reativar edição.";

              return errorMessage;
            },
          },
        }
    )
  }

  const columns = [
    {
      field: "nome",
      headerName: "Edição",
      flex: 2,
    },
    {
      field: "notaCorte",
      headerName: "Nota de Corte",
      flex: 2,
    },
    {
      field: "acoes",
      headerName: "Ações",
      flex: 1,
      align: "center" as const,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const idEdicao = params.row.id;
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            width="100%"
          >
            {ativo ? (
              <>
                <IconButton
                  onClick={() => {
                    navigate("/configuracao/edicao", { state: { idEdicao } });
                  }}
                  disabled={carregandoDelecao || carregandoReativacao}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleConfirmDelete(idEdicao)}
                  disabled={carregandoDelecao || carregandoReativacao}
                >
                  <DeleteIcon />
                </IconButton>
              </>
            ) : (
              <IconButton
                onClick={() => {
                  handleRestore(idEdicao);
                }}
                disabled={carregandoDelecao || carregandoReativacao}
              >
                <RestoreIcon />
              </IconButton>
            )}
          </Box>
        );
      },
    }];

  const rows = () => {
    return edicoesFiltradas?.map((edicao) => {
      return {
        id: edicao.idEdicao,
        nome: edicao.nome,
        notaCorte: edicao.notaCorte,
      };
    });
  };

  return (
    <>
    <Grid
      item
      xs={5.5}
      sx={{
        borderRadius: "5px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "fit-content",
      }}
    >
      <Paper sx={{ padding: "16px" }}>
        <Typography fontSize={20} fontWeight={600}>
          Edições
        </Typography>

        <Grid item xs={12} display="flex" alignItems="center" gap={2} mt={1}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="search-edição">Pesquisar por edição</InputLabel>
            <OutlinedInput
              id="search-edição"
              label="Pesquisar por edição"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end" type="submit">
                    <Search color="primary" />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                value={ativo}
                checked={ativo}
                onChange={() => setAtivo(!ativo)}
              />
            }
            label="Ativas"
            labelPlacement="end"
          />
        </Grid>

        <Grid item xs={12} sx={{ width: "100%", mt: 2 }}>
          {carregandoEdicoes ? (
            <LinearProgress />
          ) : (
            <DataGrid
              rows={rows() || []}
              columns={columns}
              pageSize={20}
              rowsPerPageOptions={[5]}
              hideFooter
              sx={{
                height: 280,
                boxShadow: 2,
                "& .MuiDataGrid-cell": {
                  fontSize: "14px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "40px",
                },
              }}
            />
          )}
        </Grid>
      </Paper>
    </Grid>
    <DeleteModal 
      open={open} 
      setOpen={setOpen} 
      title={"Confirmar desativação"} 
      context={"Realmente deseja desativar a edição?"} 
      deleteFunction={() => handleDelete(idEdicaoDelete!)}
    />
    </>
  );
};
